import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Info, ShoppingBag, Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { useState } from 'react';

const Navbar = () => {
  const location = useLocation();
  const { t, isRTL } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { name: t('home'), path: '/', icon: <Home size={18} /> },
    { name: t('products') || 'Products', path: '/products', icon: <ShoppingBag size={18} /> },
    { name: t('about'), path: '/about', icon: <Info size={18} /> },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <motion.nav
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className='sticky top-0 z-50 bg-white shadow-lg border-b border-gray-200 backdrop-blur-sm bg-white/95'
    >
      <div className='max-w-7xl mx-auto px-2 sm:px-4 lg:px-8'>
        <div className={`flex justify-between items-center h-14 sm:h-16 lg:h-20 gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Logo */}
          <Link to='/' className='flex items-center gap-1 sm:gap-2 flex-shrink-0 min-w-0 max-w-[60%] sm:max-w-none'>
            <img 
              src='logo.png?v=1.1' 
              alt={t('companyName')} 
              className='w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 object-contain flex-shrink-0' 
            />
            <div className={`min-w-0 hidden xs:block ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className={`text-xs sm:text-sm lg:text-xl font-bold text-primary-700 truncate ${isRTL ? 'font-arabic' : 'font-english'}`}>
                {t('companyName')}
              </div>
              <div className={`text-xs text-gray-600 hidden md:block truncate ${isRTL ? 'font-arabic' : 'font-english'}`}>
                {t('companyTagline')}
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className={`hidden lg:flex items-center ${isRTL ? 'space-x-reverse space-x-3 xl:space-x-6' : 'space-x-3 xl:space-x-6'}`}>
            {/* Navigation Links */}
            <div className={`flex ${isRTL ? 'space-x-reverse space-x-1 xl:space-x-4' : 'space-x-1 xl:space-x-4'}`}>
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`flex items-center gap-1 xl:gap-2 px-2 xl:px-4 py-2 rounded-lg text-xs xl:text-sm font-medium transition-all duration-300 whitespace-nowrap ${isRTL ? 'font-arabic' : 'font-english'} ${
                      isActive
                        ? 'bg-primary-500 text-white shadow-md'
                        : 'text-gray-700 hover:bg-primary-50 hover:text-primary-700'
                    }`}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    <span className="hidden xl:inline">{item.name}</span>
                  </Link>
                );
              })}
            </div>
            
            {/* Language Switcher */}
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-1 flex-shrink-0">
            <div className="flex-shrink-0">
              <LanguageSwitcher />
            </div>
            <button
              onClick={toggleMobileMenu}
              className="p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden border-t border-gray-200 bg-white"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-medium transition-all duration-300 ${isRTL ? 'font-arabic flex-row-reverse' : 'font-english'} ${
                      isActive
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-primary-50 hover:text-primary-700'
                    }`}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;
